import { Card, Classes, Colors, Spinner } from "@blueprintjs/core";
import { DayPilotCalendar } from "@daypilot/daypilot-lite-react";
import {
  add,
  addHours,
  eachDayOfInterval,
  format,
  isSameDay,
  setHours,
  setMinutes,
  startOfDay,
  startOfWeek
} from "date-fns";
import { fr } from "date-fns/locale";
import { darken, lighten } from "polished";
import * as React from "react";
import { useQuery } from "react-query";
import { useParams } from "react-router";
import styled from "styled-components";
import { UtilisateurApi, UserAvailabilityDtoFromJSON } from "../../../api";
import { WeekPicker } from "../../../components";
import { useAuth } from "../../../contexts";
import { useApiService, useManageError, useTheme } from "../../../hooks";
import { IThemeData } from "../../../theme";

const Container = styled.div<{ theme: IThemeData }>`
  display: flex;
  flex-direction: column;
  margin: 1rem 2rem;
  position: relative;

  & .calendar_default_main {
    border-radius: 0.35rem !important;
    border-right: none;
    border-bottom: none;
  }

  & .calendar_default_corner_inner,
  .calendar_default_colheader_inner {
    background: ${Colors.WHITE} !important;
    color: ${props => props.theme.mainTextColor} !important;
  }

  & .calendar_default_rowheader_inner {
    background: ${Colors.WHITE} !important;
    color: ${props => props.theme.mainTextColor} !important;
  }

  & .calendar_default_main > div {
    overflow: hidden !important;
  }
  & .calendar_default_main {
    & > div:first-of-type {
      border-top-left-radius: 0.35rem !important;
      border-top-right-radius: 0.35rem !important;
      & > table > tbody > tr > td:nth-child(2) > table > tbody > tr > td:nth-child(5) {
        border-top-right-radius: 0.35rem !important;
        & div {
          border-top-right-radius: 0.35rem !important;
        }
      }
    }

    & > div:nth-child(2) {
      border-bottom-left-radius: 0.35rem !important;
      & > div > table > tbody > tr {
        & > td:nth-child(1) > table > tbody > tr:nth-child(19) {
          border-bottom-left-radius: 0.35rem !important;
          & > td {
            border-bottom-left-radius: 0.35rem !important;
            & > div {
              border-bottom-left-radius: 0.35rem !important;
              & > div {
                border-bottom-left-radius: 0.35rem !important;
              }
            }
          }
        }
        & > td:nth-child(2) > div > table > tbody > tr:nth-child(38) {
          border-bottom-right-radius: 0.35rem !important;
          > td:last-child {
            border-bottom-right-radius: 0.35rem !important;
            div {
              border-bottom-right-radius: 0.35rem !important;
            }
          }
        }
      }
    }
  }

  & .calendar_default_event_inner {
    border: solid 1px;
    border-radius: 0.35rem !important;
    font-size: 10pt;
    font-weight: 500;
    padding: 10px;
    margin: 1px;
    overflow: hidden;
  }

  & .calendar_default_event_inner:hover {
    cursor: pointer;
  }

  & .calendar_default_event.disabled .calendar_default_event_inner {
    cursor: default !important;
  }
`;

const SpinnerContainer = styled.div`
  position: absolute;
  z-index: 9999;
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
`;

const StyleContainer = styled.div`
  & *:focus {
    outline: none !important;
  }
  & .${Classes.OVERLAY} {
    width: 100% !important;
    height: 45%;
    top: auto !important;
  }
`;

export interface IProfileAbsencesPageProps {}

export const ProfileAbsencesPage: React.FunctionComponent<IProfileAbsencesPageProps> = props => {
  const { theme } = useTheme();
  const api = useApiService(UtilisateurApi);
  const { user } = useAuth();
  const { manageError } = useManageError();
  const { id } = useParams<{ id: string; tab: string; state: string }>();
  const iduser = React.useMemo(() => +id, [id]);
  const calendarRef = React.useRef<any>();

  const [selectedDate, setSelectedDate] = React.useState<Date>(startOfDay(new Date()));

  const { data: defaultAvailability, isFetching } = useQuery(
    ["user-default-availability"],
    async () => await api.utilisateurDefaultAvailability()
  );

  const { data: userAvailability, isFetching: isFetchingUser, refetch } = useQuery(
    ["user-availability", iduser, selectedDate],
    async () => await api.utilisateurAvailability({ iduser, body: startOfWeek(selectedDate) })
  );

  const weekDays = React.useMemo(
    () =>
      eachDayOfInterval({
        start: startOfWeek(selectedDate),
        end: add(startOfWeek(selectedDate), { days: 5 })
      }),
    [selectedDate]
  );

  const events = React.useMemo(() => {
    if (!userAvailability || !defaultAvailability) return;

    const allAvailability = [];
    weekDays.forEach(wd => {
      allAvailability.push(
        ...defaultAvailability.map(a => ({
          ...a,
          start: setMinutes(setHours(wd, +a.startTime.split(":")[0]), +a.startTime.split(":")[1]),
          end: setMinutes(setHours(wd, +a.endTime.split(":")[0]), +a.endTime.split(":")[1])
        }))
      );
    });

    return allAvailability.map((da, i) => {
      const ua = userAvailability.find(a => a.idavailability === da.idavailability && isSameDay(a.jour, da.start));
      const available = !!ua ? !da.defaultAvailability : da.defaultAvailability;
      const color = available ? Colors.GREEN5 : Colors.RED5;

      return {
        id: i,
        text: da.label,
        start: format(da.start, "yyyy-MM-dd'T'kk:mm:ss"),
        end: format(da.end, "yyyy-MM-dd'T'kk:mm:ss"),
        backColor: lighten(0.12, color),
        fontColor: darken(0.4, color),
        borderColor: darken(0.1, color),
        barHidden: true,
        toolTip: "",
        tags: {
          start: da.start,
          end: da.end,
          details:
            ua ??
            UserAvailabilityDtoFromJSON({
              idavailability: da.idavailability,
              iduser: iduser,
              jour: addHours(startOfDay(da.start), 1)
            }),
          available
        }
      };
    });
  }, [defaultAvailability, iduser, userAvailability, weekDays]);

  React.useEffect(() => {
    if (!calendarRef.current || !userAvailability || !defaultAvailability) return;
    calendarRef.current.control.update({
      startDate: format(startOfWeek(selectedDate, { locale: fr }), "yyyy-MM-dd'T'kk:mm:ss"),
      events: events
    });

    calendarRef.current.control.events.update(events);
  }, [defaultAvailability, events, selectedDate, userAvailability]);

  const [saving, setSaving] = React.useState(false);
  const onEventClick = React.useCallback(
    async args => {
      if (user.iduser !== iduser) return;

      try {
        setSaving(true);
        const dto = args.e.data.tags.details;
        await api.utilisateurSaveAvailability({ UserAvailabilityDto: UserAvailabilityDtoFromJSON(dto) });
      } catch (e) {
        manageError(e);
      } finally {
        setSaving(false);
      }
      refetch();
    },
    [api, iduser, manageError, refetch, user.iduser]
  );

  const [config] = React.useState({
    locale: "fr-be",
    viewType: "WorkWeek" as any,
    cellHeight: 23,
    businessBeginsHour: 7,
    businessEndsHour: 19,
    timeRangeSelectedHandling: "Disabled" as any,
    eventMoveHandling: "Disabled" as any,
    eventResizeHandling: "Disabled" as any,
    eventClickHandling: "Enabled" as "Enabled" | "Disabled" | "CallBack" | "ContextMenu",
    onEventClick: onEventClick,
    onBeforeEventRender: args => {
      args.data.cssClass = user.iduser !== iduser && "disabled";
    }
  });

  return (
    <StyleContainer>
      <Container theme={theme}>
        <WeekPicker selected={selectedDate} onSelect={setSelectedDate} />
        {(isFetching || isFetchingUser || saving) && (
          <SpinnerContainer>
            <Card>
              <Spinner />
            </Card>
          </SpinnerContainer>
        )}
        <DayPilotCalendar {...config} ref={calendarRef} />
      </Container>
    </StyleContainer>
  );
};

export default ProfileAbsencesPage;
